//#region Imports and Global Definitions
import React, { useState, useEffect, useRef } from 'react';
import { StyleSheet, Animated, View, SafeAreaView, Platform, useWindowDimensions, Pressable, FlatList, TouchableOpacity } from 'react-native';
import { useTheme } from '@react-navigation/native';
import { createCommonStyles } from '../../../../styles/commonStyles';
import CustomLabel from '../../../../components/CustomLabel/CustomLabel';
import CustomInput from '../../../../components/CustomInput';
import CustomButton from '../../../../components/CustomButton';
import { ActivityIndicator, Card } from 'react-native-paper';
import { showMessage } from "react-native-flash-message";
import HRAdminLocationsScreen from './HRAdminLocationsScreen';
import Ionicons from "@react-native-vector-icons/ionicons/static";
import { useCompany } from '../../../../hooks/useCompany';
import { useSettingsContext } from '../../../../hooks/useSettingsContext';

var _ = require('lodash');

// * Language
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';

const HRAdminBranchesScreen = ({ selectedCompany, showLocations, setShowLocations }) => {

    //#region Const Definitions
    const { width, height } = useWindowDimensions();
    const { d365 } = useSettingsContext()
    const isPlatformWeb = (Platform.OS === 'web' && width > 992 ? false : true);
    const { colors } = useTheme();
    const styles = makeStyles(colors, width, height, isPlatformWeb);
    const common = createCommonStyles(colors);
    const [isLoading, setIsLoading] = useState(true);
    const [branchesData, setBranchesData] = useState([]);
    const [changes, setChanges] = useState({});
    const [noChangesBranches, setNoChangesBranches] = useState([]);
    const [hasNotProvidedCode, setHasNotProvidedCode] = useState();
    const [selectedBranch, setSelectedBranch] = useState(null);
    const [branchesButtonStates, setBranchesButtonStates] = useState({});
    const [isDuplicate, setIsDuplicate] = useState();
    const [branchesCnt, setBranchesCnt] = useState(0);
    const [refreshAfterSave, setRefreshAfterSave] = useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const { getbranchesData, updateBranch, insertBranch, deleteBranch } = useCompany();
    const companyRef = useRef(null);
    const ref = useRef(null);
    //#endregion

    // * Language
    const { t, i18n } = useTranslation();

    //#region UseEffect

    useEffect(() => {
        if (!refreshAfterSave && companyRef.current === selectedCompany) return;
        companyRef.current = selectedCompany;
        getbranchesData(selectedCompany)
            .then((arr) => {
                setChanges({});
                setBranchesData(arr);
                setNoChangesBranches([...arr])
                setHasNotProvidedCode(Array(arr.length).fill(false))
                setBranchesButtonStates(Array(arr.length).fill(false))
                setIsDuplicate(Array(arr.length).fill(false))
                setBranchesCnt(arr.length);
                setRefreshAfterSave(false);
                setIsLoading(false);
            })
            .catch(() => {
                setRefreshAfterSave(false);
                setIsLoading(false);
            })
    }, [selectedCompany, refreshAfterSave])

    //#endregion

    //#region Listeners

    const handleEditPress = (index, item, key, value) => {
        if (item.index in changes) {
            changes[item.index][key] = value;
        }
        else {
            changes[item.index] = { ...item };
            changes[item.index][key] = value;
        }
        if (changes[item.index].isNewBranch === true) changes[item.index].oldBranchID = "";
        branchesData[index] = { ...branchesData[index], [key]: value }

    }

    const checkForDuplicates = (insertData, updateData) => {
        let duplicateCodes = [];

        const setNoChangesBranches = new Set(noChangesBranches.map((obj) => obj.branch.toUpperCase()));

        insertData.forEach((insertBranch) => {
            if (setNoChangesBranches.has(insertBranch.branch.toUpperCase())) {
                insertBranch['status'] = 'INSERT';
                insertBranch['index'] = insertBranch.index;
                duplicateCodes.push(insertBranch);
            }
            else if (isDuplicate[insertBranch.index])
                isDuplicate[insertBranch.index] = false;
        })

        updateData.forEach(updateBranch => {
            if (setNoChangesBranches.has(updateBranch.branch.toUpperCase()) && updateBranch.oldBranchID.toUpperCase() !== updateBranch.branch.toUpperCase()) {
                updateBranch['status'] = 'UPDATE';
                updateBranch['index'] = updateBranch.index;
                duplicateCodes.push(updateBranch);
            }
            else if (updateBranch.oldBranchID.toUpperCase() !== updateBranch.branch.toUpperCase() && isDuplicate[updateBranch.index])
                isDuplicate[updateBranch.index] = false;
        })
        return duplicateCodes;
    };

    const handleDuplicates = (duplicates) => {
        showMessage({
            message: t('common:duplicateBranchesMessage'),
            type: "error",
            duration: 3000,
        })
        let prevStateDuplicate = isDuplicate;
        duplicates.forEach(duplicate => {
            prevStateDuplicate[duplicate.index] = true;
        })
        setIsDuplicate([...prevStateDuplicate]);
        return;
    }

    const handleSave = async () => {
        let insertBranches = []
        let updateExistingBranches = []
        let deleteBranches = []

        let insertBranchesDupl = []; let updateBranchesDupl = [];
        let newPromises = [];

        let deleteSucc = { status: true, message: "" };
        let insertSucc = true;
        let updateSucc = true;

        let updatedArray = [...hasNotProvidedCode];
        let emptyCodeFound = false;

        const hasEmptyLine = branchesData.some(branch =>
            branch.branch === "" &&
            branch.description === "" &&
            branch.regionalCode === "" &&
            branch.d365Code === ""
        )
        Object.entries(changes).forEach(([index, branch]) => {
            if (branch.branch == "") {
                updatedArray[branch.index] = true;
                emptyCodeFound = true;
            }
            else updatedArray[branch.index] = false;
        });

        setHasNotProvidedCode([...updatedArray]);

        Object.values(changes).forEach((change) => {
            const branch = {
                company: selectedCompany,
                branch: change.branch,
                description: change.description,
                regionalCode: change.regionalCode,
                d365Code: change['d365Code'],
                oldBranchID: change.oldBranchID
            }
            if (change.isDeleted === true)
                deleteBranches.push(branch);
            else if (change.isNewBranch === true) {
                insertBranches.push(branch);
                insertBranchesDupl.push(change);
            }
            else {
                updateExistingBranches.push(branch);
                updateBranchesDupl.push(change);
            }
        });

        let duplicates = checkForDuplicates(insertBranchesDupl, updateBranchesDupl);
        if (duplicates.length > 0) {
            handleDuplicates(duplicates);
            return;
        }

        if (emptyCodeFound) {
            showMessage({
                message: t('common:mandatoryCodeNotGivenBranches'),
                type: "danger",
                duration: 3000,
            })
            return;
        }

        if (updateExistingBranches.length > 0) {
            newPromises.push(updateBranch(updateExistingBranches, updateSucc));
            updateBranch(updateExistingBranches, updateSucc)
                .then((res) => {
                    if (!res) updateSucc = false;
                    removeFromChangesAfterSave(updateExistingBranches);
                })
                .catch(() => { });
        }
        if (insertBranches.length > 0) {
            newPromises.push(insertBranch(insertBranches, insertSucc));
            insertBranch(insertBranches, insertSucc)
                .then((res) => {
                    if (!res) insertSucc = false;
                    removeFromChangesAfterSave(insertBranches);

                }).catch(() => { });
        }
        if (deleteBranches.length > 0) {
            newPromises.push(deleteBranch(deleteBranches, deleteSucc));
            deleteBranch(deleteBranches, deleteSucc)
                .then((res) => {
                    if (!res) {
                        deleteSucc['status'] = false;
                        deleteSucc['message'] = error.message;
                    }
                    removeFromChangesAfterSave(deleteBranches);
                })
                .catch(() => { })
        }

        try {
            await Promise.all(newPromises).then(() => {
                if (!insertSucc || !deleteSucc['status'] || !updateSucc) {
                    if (!deleteSucc['status'] && deleteSucc['message'] !== "") {
                        showMessage({
                            message: t('common:branchWithLocationDeleteMess'),
                            type: "danger",
                            duration: 3000,
                        })
                        let removeDeleteBranches = Object.fromEntries(Object.entries(changes).filter(([key, value]) =>
                            value.isDeleted !== true)
                        )
                        setChanges(removeDeleteBranches);
                    }
                    else
                        showMessage({
                            message: t('common:issueMessage'),
                            type: "error",
                            duration: 3000,
                        })

                }
                else if (insertSucc && deleteSucc['status'] && updateSucc) {
                    showMessage({
                        message: t('common:allOperationsSuccMess'),
                        type: "success",
                        duration: 3000,
                    })
                    setRefreshAfterSave(true);
                }
            });
        } catch (error) {
            console.log(error)
            setRefreshAfterSave(true);
        }
    }

    const removeFromChangesAfterSave = (branchesSavedChanges) => {
        Object.entries(changes).forEach(([index, branch]) => {
            branchesSavedChanges.forEach(branchSaved => {
                if (branch.index == branchSaved.index) {
                    delete changes[branch.index];
                }
            })
        });
    }

    const handleDeleteBranch = (branch, index) => {
        if (!(branch.index in changes)) {
            setChanges((prevState) => ({
                ...prevState,
                [branch.index]: {
                    branch: branch.branch, description: branch.description, company: selectedCompany,
                    d365Code: branch.d365Code, regionalCode: branch.regionalCode, isDeleted: true, index: branch.index
                }
            }));
        }
        else {
            changes[branch.index]["isDeleted"] = true
        }
        if (isDuplicate[branch.index]) {
            const updatedBranches = [...isDuplicate];
            updatedBranches[branch.index] = false;
            setIsDuplicate(updatedBranches);
        }
        if (hasNotProvidedCode[branch.index]) {
            const updatedBranches = [...hasNotProvidedCode];
            updatedBranches[branch.index] = false;
            setHasNotProvidedCode(updatedBranches);
        }
        let array = [...branchesData];
        array.splice(index, 1);
        setBranchesData(array);
        setBranchesCnt(branchesCnt - 1);
    }

    const handleAddNewBranch = () => {
        let array = [];
        const hasEmptyLine = branchesData.some(branch =>
            branch.branch === "" &&
            branch.description === "" &&
            branch.regionalCode === "" &&
            branch.d365Code === ""
        )
        if (hasEmptyLine) {
            showMessage({
                message: t('common:multEmptyLines'),
                type: "danger",
                duration: 10000,
            })
        }
        else {
            let item = {
                branch: "",
                company: selectedCompany,
                regionalCode: "",
                description: "",
                d365Code: "",
                isNewBranch: true,
                oldBranchID: "",
                locationsCnt: 0,
                index: branchesCnt
            }
            array = [item, ...branchesData];
            changes[item.index] = { ...item };
            changes[branchesCnt]['branch'] = "";
            setBranchesCnt(branchesCnt + 1);
            scrollToTop();
            setIsDuplicate(prevState => [...prevState, false]);
            setBranchesData(array);
        }
    }
    const handleCancelChanges = () => {

        setBranchesData([...noChangesBranches]);
        setIsDuplicate(Array(noChangesBranches.length).fill(false))
        setHasNotProvidedCode(Array(noChangesBranches.length).fill(false))
        setChanges({})
    }
    const scrollToTop = () => {
        ref.current?.scrollToOffset({ y: 0, animated: true })
    }

    const handlePress = (item, index) => {
        if (!showLocations) {
            setShowLocations(!showLocations);
        }
        setSelectedBranch(item.branch);
        if (selectedBranch === item.branch && showLocations)
            return;
        setBranchesButtonStates((prevState) => {
            const newState = Object.keys(prevState).reduce((acc, key) => {
                acc[key] = false;
                return acc;
            }, {});
            newState[index] = !prevState[index];
            return newState;
        });
    }

    const handleGoBack = () => {
        if (showLocations) {
            setShowLocations(false);
            setBranchesButtonStates((prevState) => {
                const newState = Object.keys(prevState).reduce((acc, key) => {
                    acc[key] = false;
                    return acc;
                }, {});
                return newState;
            });
            setRefreshAfterSave(true);
        }
    }
    //#endregion


    //#region Components

    const BranchListView = ({ item, index }) => {
        return (
            <SafeAreaView>
                {showLocations ?
                    <SafeAreaView
                        style={[{ alignContent: 'center' }]}>
                        <CustomLabel
                            value={item.description === "" ? item.branch : item.description}
                            type="BRANCHES_WHEN_SHOWING_LOCATIONS" />

                    </SafeAreaView>
                    :
                    <SafeAreaView style={styles.row}>
                        <Animated.View
                            style={[styles.item, { justifyContent: 'flex-start' }]}>
                            <CustomInput
                                defaultValue={item.branch}
                                setValue={(e) => handleEditPress(index, item, 'branch', e)}
                                maxLen={5}
                                type={hasNotProvidedCode[item.index] || isDuplicate[item.index] ? "MISSING_BRANCHES_CODE" : "MAININPUT"}
                                editable={showLocations ? false : true}
                            />
                        </Animated.View>
                        <Animated.View
                            style={[styles.item]}>
                            <CustomInput
                                defaultValue={item.description}
                                setValue={(e) => handleEditPress(index, item, 'description', e)}
                                maxLen={50}
                                type="MAININPUT"
                                editable={showLocations ? false : true}
                            />
                        </Animated.View>
                        <Animated.View
                            style={[styles.item]}>
                            <CustomInput
                                defaultValue={item.regionalCode}
                                setValue={(e) => handleEditPress(index, item, 'regionalCode', e)}
                                maxLen={5}
                                type="MAININPUT"
                                editable={showLocations ? false : true}
                            />
                        </Animated.View>
                        {/* // * Show based on settings  */}
                        {d365 &&
                            <Animated.View
                                style={[styles.item]}>
                                <CustomInput
                                    defaultValue={item["d365Code"]}
                                    setValue={(e) => handleEditPress(index, item, 'd365Code', e)}
                                    maxLen={50}
                                    type="MAININPUT"
                                    editable={showLocations ? false : true}
                                />
                            </Animated.View>
                        }
                        <SafeAreaView>
                            {item.locationsCnt <= 0 &&
                                <CustomButton
                                    iconName='trash-outline'
                                    onPress={() => {
                                        handleDeleteBranch(item, index)
                                    }}
                                    show='HOME_BELL'
                                    type='HOME_BELL'
                                    textTop='2'
                                    sz={30} />
                            }
                            {item.isNewBranch !== true &&
                                <CustomButton
                                    iconName='location-outline'
                                    onPress={() => {
                                        handlePress(item, index)
                                    }}
                                    show='HOME_BELL'
                                    type='HOME_BELL'
                                    textTop='2'
                                    sz={30} />
                            }
                        </SafeAreaView>
                    </SafeAreaView>
                }
            </SafeAreaView>
        )
    }

    const renderBranches = (item, index) => {
        if (showLocations)
            return renderPressableBranches(item, index);
        return (
            <Card
                style={[common.cardstyle]} >
                <Card.Content>
                    <BranchListView item={item} index={index} />
                </Card.Content>
            </Card >
        )

    };

    const renderPressableBranches = (item, index) => {
        if (hasUnsavedChanges)
            return (
                <View
                    style={styles.button}>
                    <TouchableOpacity
                        onPress={() => {
                            if (Platform.OS === 'web') {
                                // * Note: below styles do not seem to be working. Maybe investigate?
                                Swal.fire({
                                    title: `<text style="font-family:'Roboto-Medium', font-size:16px">${t('common:changeBranchesMessage')}</text>`,
                                    icon: "warning",
                                    showCancelButton: true,
                                    reverseButtons: true,
                                    cancelButtonText: `<text style="font-family:'Roboto-Medium', font-size:16px">${t('common:cancel')}</text>`,
                                    cancelButtonColor: '#d3d3d3',
                                    confirmButtonText: `<text style="font-family:'Roboto-Medium', font-size:16px">${t('common:confirmMessage')}</text>`,
                                    confirmButtonColor: '#A0A2FF',
                                }).then((result) => {
                                    if (result.isConfirmed) {
                                        setHasUnsavedChanges(false);
                                        handlePress(item, index);
                                    };
                                });
                            }
                        }}>
                        <View>
                            <Card
                                style={[common.cardstyle, branchesButtonStates[index] === true ? [common.cardstyle, styles.Button_SELECTED] : null]} >
                                <Card.Content>
                                    <BranchListView item={item} index={index} />
                                </Card.Content>
                            </Card>
                        </View>
                    </TouchableOpacity>
                </View >
            )
        else return (
            <Pressable onPress={() => { handlePress(item, index) }}>
                <Card
                    style={[common.cardstyle, branchesButtonStates[index] === true ? [common.cardstyle, styles.Button_SELECTED] : null]} >
                    <Card.Content>
                        <BranchListView item={item} index={index} />
                    </Card.Content>
                </Card>
            </Pressable>
        )
    };

    const checkIfUnsavedChanges = (hasChanges) => {
        setHasUnsavedChanges(hasChanges);
    }
    //#endregion

    //#region Loading
    if (isLoading)
        return (
            <View style={styles.loader}>
                <ActivityIndicator animating={true} color={colors.blue} />
            </View>
        )
    //#endregion

    return (
        <SafeAreaView style={styles.mainView}>
            {showLocations &&
                <TouchableOpacity onPress={() => { handleGoBack() }} style={styles.backButton}>
                    <Ionicons name="arrow-back" size={30} color="black" />
                </TouchableOpacity>
            }
            <SafeAreaView style={styles.rowView}>
                <SafeAreaView style={[styles.subMain]}>
                    {selectedCompany &&
                        <View style={styles.innerView}>
                            {!showLocations &&
                                <View style={[styles.buttons, styles.functionalityButtons]}>
                                    <CustomButton
                                        text={t('common:new')}
                                        onPress={() => { handleAddNewBranch(selectedBranch) }}
                                        type='BUTTON_ADD'
                                        textTop='0'
                                        sz={15}
                                    />
                                    {((Object.keys(changes).length > 0 && Object.values(changes).some(innerObj => !innerObj.hasOwnProperty('isNewBranch'))) || branchesData.length > 0) &&
                                        <CustomButton
                                            text={t('common:saveChanges')}
                                            onPress={() => { handleSave() }}
                                            type='BUTTON_SAVE'
                                            isGradient={true}
                                            textTop='0'
                                            sz={15}
                                        />
                                    }
                                    <CustomButton
                                        text={t('common:cancel')}
                                        onPress={() => { handleCancelChanges() }}
                                        type="BUTTON_CANCEL"
                                        textTop='0'
                                        sz={15}
                                    />
                                </View>
                            }
                            <View style={styles.branchesView}>
                                <FlatList
                                    style={showLocations ? styles.fullHeight : styles.fullWidth}
                                    data={branchesData}
                                    keyExtractor={(item, index) => `${item.branch}-${index}`}
                                    renderItem={({ item, index }) =>
                                        renderBranches(item, index)
                                    }
                                    scrollEnabled={true}
                                    refreshing={isLoading}
                                    ref={ref}
                                    ListHeaderComponent={
                                        branchesData.length > 0 &&
                                        <SafeAreaView style={common.listTitle}>
                                            {showLocations ? <></> :
                                                <>
                                                    <View style={[styles.row, { marginRight: '7%', }]}>
                                                        <CustomLabel
                                                            value={t('common:branch')}
                                                            type='SUBTITLE' />

                                                        <CustomLabel
                                                            value={t('common:description')}
                                                            type='SUBTITLE' />

                                                        <CustomLabel
                                                            value={t('common:regCode')}
                                                            type='SUBTITLE' />

                                                        {d365 &&
                                                            <CustomLabel
                                                                value={t('common:d365Code')}
                                                                type='SUBTITLE' />
                                                        }
                                                    </View>
                                                    <SafeAreaView style={common.divider} />
                                                </>}
                                        </SafeAreaView>
                                    }
                                    initialNumToRender={4}
                                    ListEmptyComponent={
                                        isLoading ?
                                            <ActivityIndicator animating={true} color={colors.blue} />
                                            :
                                            <CustomLabel
                                                value={t("common:branchesLoadingPreview")}
                                                type="NO_BRANCHES"
                                            />
                                    }
                                />
                                {showLocations ?
                                    <SafeAreaView style={styles.locationView}>
                                        <HRAdminLocationsScreen
                                            props={{
                                                selectedCompany: selectedCompany,
                                                selectedBranch: selectedBranch,
                                                checkIfUnsavedChanges: checkIfUnsavedChanges,
                                                handleGoBack: handleGoBack
                                            }}
                                        />
                                    </SafeAreaView>
                                    : <></>
                                }
                            </View>

                        </View>
                    }
                </SafeAreaView>
            </SafeAreaView >
        </SafeAreaView >
    )
};

//#region Styles
const makeStyles = (colors, width) => StyleSheet.create({
    mainView: {
        flex: 1,
        marginHorizontal: 10,
    },
    subMain: {
        borderRadius: 35,
        elevation: (Platform.OS === 'web' && width > 992 ? 3 : 0),
        shadowColor: colors.card_line,
        shadowOffset: { width: 0, height: 0 },
        shadowOpacity: 0.1,
        width: '100%'
    },
    rowView: {
        flex: 1,
        flexDirection: 'row',
        justifyContent: 'center'
    },
    cardstyleCompany: {
        margin: 10,
        borderRadius: 5,
        backgroundColor: colors.background_card,
    },
    row: {
        flex: 1,
        flexDirection: "row",
        alignItems: 'center',
    },
    item: {
        flex: 1,
    },
    error: {
        color: colors.validation_error,
        alignSelf: 'center',
        fontSize: 14,
    },
    Button_SELECTED: {
        borderWidth: 3,
        borderColor: colors.buttons_submit,
    },
    backButton: {
        alignSelf: 'flex-end'
    },
    innerView: {
        flex: 1,
    },
    branchesView: {
        flexDirection: 'row',
        height: '100%'
    },
    fullHeight: {
        height: '95%'
    },
    fullWidth: {
        height: '78%'
    },
    locationView: {
        width: '85%',
        height: '100%',
        marginHorizontal: 10,
    },
    functionalityButtons: {
        justifyContent: 'flex-end',
    },
    buttons: {
        flexDirection: 'row',
        justifyContent: 'flex-end',
        alignSelf: 'flex-end',
    },
    loader: {
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: '4%'
    },
});


//#endregion

export default HRAdminBranchesScreen;